import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { 
  CheckCircle, 
  Info, 
  AlertTriangle, 
  XCircle, 
  Trophy, 
  TrendingDown,
  DollarSign 
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { Notification } from "@/hooks/useNotifications";

interface NotificationItemProps {
  notification: Notification;
  onClick?: () => void;
}

export function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const getTypeIcon = (type: Notification['type']) => {
    const iconProps = { className: "h-5 w-5" };
    
    switch (type) {
      case 'success':
      case 'goal_achieved':
        return <Trophy {...iconProps} />;
      case 'warning':
      case 'high_churn':
        return <TrendingDown {...iconProps} />;
      case 'error':
        return <XCircle {...iconProps} />;
      case 'low_revenue':
        return <DollarSign {...iconProps} />;
      default:
        return <Info {...iconProps} />;
    }
  };

  const getTypeColorClass = (type: Notification['type']) => {
    switch (type) {
      case 'success':
      case 'goal_achieved':
        return 'bg-success/10 text-success';
      case 'warning':
      case 'high_churn':
        return 'bg-warning/10 text-warning';
      case 'error':
        return 'bg-destructive/10 text-destructive';
      case 'low_revenue':
        return 'bg-primary/10 text-primary';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        "p-3 rounded-lg border transition-smooth cursor-pointer hover:shadow-sm",
        !notification.is_read && "bg-primary/5 border-primary/20"
      )}
    >
      <div className="flex gap-3">
        <div className={cn(
          "h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0",
          getTypeColorClass(notification.type)
        )}>
          {getTypeIcon(notification.type)}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-medium text-sm">{notification.title}</h4>
            {!notification.is_read && (
              <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1" />
            )}
          </div>
          
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
            {notification.message}
          </p>
          
          <p className="text-xs text-muted-foreground mt-1">
            {formatDistanceToNow(new Date(notification.created_at), { 
              addSuffix: true, 
              locale: ptBR 
            })}
          </p>
        </div>
      </div>
    </div>
  );
}
